import { useEffect, useRef } from "react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Closing";
import { StepAssessment, StepAppointment, StepDetails } from "@/components/booking/Steps";
import { StepAddons } from "@/components/booking/Addons";
import { StepPayment } from "@/components/booking/Payment";
import { Summary, MobileSummaryBar } from "@/components/booking/Summary";
import { BookingIntro, StepHeader, StepFooter, Confirmation, WaitlistForm } from "@/components/booking/Shell";
import { useBooking } from "@/hooks/useBooking";

const useScrollToCardOnChange = (ref, deps) => {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const top = (ref.current?.getBoundingClientRect().top || 0) + window.scrollY - 80;
    window.scrollTo({ top: Math.max(top, 0), behavior: reduce ? "auto" : "smooth" });
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};

const VIEWS = [StepAssessment, StepAddons, StepAppointment, StepDetails, StepPayment];

const StepBody = ({ b }) => {
  const View = VIEWS[b.step];
  const last = b.step === VIEWS.length - 1;
  return (
    <>
      <StepHeader step={b.step} />
      <div className="mt-5 sm:mt-6"><View data={b.data} set={b.set} errors={b.shownErrors} touch={b.touch} toggleAddon={b.toggleAddon} note={b.addonNote} price={b.price} /></div>
      {b.step === 0 && b.outside ? (
        <WaitlistForm zip={b.data.zip} onSubmit={b.joinWaitlist} busy={b.submitting} error={b.error} done={b.waitlisted} />
      ) : (
        <StepFooter step={b.step} onBack={b.back} onNext={b.next} disabled={!b.canProceed} label={b.continueLabel} busy={b.submitting} error={b.error} secure={last} />
      )}
    </>
  );
};

export default function Book() {
  const b = useBooking();
  const card = useRef(null);
  useScrollToCardOnChange(card, [b.step, b.result]);
  const hideBar = Boolean(b.result) || b.step === VIEWS.length - 1 || (b.step === 0 && b.outside);

  return (
    <div className="min-h-screen bg-white" data-testid="booking-page">
      <Nav />
      <main className="container-x py-8 sm:py-14 lg:py-16">
        <BookingIntro />
        <div className="mt-7 sm:mt-10 grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
          <section ref={card} className="lg:col-span-7 min-w-0 rounded-md border-0 sm:border border-divider bg-white p-0 sm:p-8 scroll-mt-20" aria-labelledby="step-title">
            {b.result ? <Confirmation r={b.result} /> : <StepBody b={b} />}
          </section>
          <div className="lg:col-span-5 min-w-0 lg:sticky lg:top-28"><Summary data={b.data} /></div>
        </div>
      </main>
      <Footer />
      <MobileSummaryBar data={b.data} hidden={hideBar} />
    </div>
  );
}
